import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Wheat, BookOpen, Heart, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import EmailSignupSection from '../components/EmailSignupSection';
import HomeBannerSection from '../components/HomeBannerSection';
import HomeKitVideoSection from '../components/HomeKitVideoSection';
import homeHero from '../assets/home_hero.jpg';

const Home = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const scrollTarget = params.get('scroll');
    if (scrollTarget !== 'email-signup') return;

    const el = document.getElementById('email-signup');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Clean the URL so it doesn't keep scrolling on refresh
      window.history.replaceState({}, '', '/');
    }
  }, [location.search]);


  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Hero Section with Product Photo */}
      <section className="relative bg-gradient-to-b from-white to-[#f7f5f2] py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center space-x-2 bg-[#a8b5a0]/10 px-4 py-2 rounded-full mb-6">
                <Wheat className="h-4 w-4 text-[#a8b5a0]" />
                <span className="text-sm font-medium text-[#736c64]">Welcome to Hearthlowe</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e1919] mb-6 leading-tight">
                Purely Crafted
              </h1>
              
              <p className="text-xl md:text-2xl text-[#736c64] mb-6 leading-relaxed">
                Your journey to artisan sourdough begins here.
              </p>

              <p className="text-base md:text-lg text-[#736c64] mb-8 leading-relaxed">
                Whether you just unboxed your Hearthlowe kit or you&apos;re here to explore, welcome! 
                This is your resource hub—guides, recipes, and support to help you bake beautiful sourdough at home.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/getting-started">
                  <Button 
                    size="lg" 
                    className="bg-[#a8b5a0] hover:bg-[#8fa087] text-white px-8 py-6 text-lg group w-full sm:w-auto"
                  >
                    Start Your Journey
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/recipes">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-[#736c64] text-[#736c64] hover:bg-white px-8 py-6 text-lg w-full sm:w-auto"
                  >
                    View Recipes
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Column - Product Photo */}
            <div className="order-1 lg:order-2">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={homeHero}
                  alt="Hearthlowe Sourdough Baking Kit"
                  className="w-full h-[320px] sm:h-[420px] lg:h-[520px] object-cover object-[50%_60%]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Don't Have a Kit Yet? CTA */}
      <section className="bg-gradient-to-r from-[#1e1919] to-[#2d2626] py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#a8b5a0]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Package className="h-6 w-6 text-[#a8b5a0]" />
              </div>
              <div className="text-center sm:text-left">
                <p className="text-lg font-semibold text-white">Don&apos;t have your kit yet?</p>
                <p className="text-sm text-[#bbb5ae]">Start your sourdough journey with the Hearthlowe Baking Kit</p>
              </div>
            </div>
            <a
              href="https://www.amazon.com/s/ref=nb_sb_ss_i_1_0?url=search-alias%3Daps&field-keywords=sourdough+baking+kit&field-asin=B0G29RQGT9&rh=i:aps,ssx:relevance"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#a8b5a0] hover:bg-[#8fa087] text-white px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap shadow-lg"
              data-testid="get-kit-amazon-btn"
            >
              Shop on Amazon
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <HomeBannerSection />
      <HomeKitVideoSection />

      {/* What's Inside Your Kit */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e1919] mb-4">
              Your Hearthlowe Kit Essentials
            </h2>
            <p className="text-lg text-[#736c64] max-w-2xl mx-auto">
              A curated set of tools to get you started—paired with the guidance and support most kits don’t include.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-xl bg-[#f7f5f2] p-4 sm:p-6 md:p-8">
              <img
                src="https://customer-assets.emergentagent.com/job_dough-journey-1/artifacts/m0aryvix_81pfrsKwpNL._AC_SL1500_.jpg"
                alt="Hearthlowe Sourdough Baking Kit Contents"
                className="w-full max-w-[576px] mx-auto rounded-lg"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#a8b5a0]/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Package className="h-6 w-6 text-[#a8b5a0]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1e1919] mb-2">
                Premium Tools
              </h3>
              <p className="text-[#736c64]">
                Banneton baskets, dough whisk, lame, scrapers, and more—all carefully selected for quality.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-[#a8b5a0]/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-6 w-6 text-[#a8b5a0]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1e1919] mb-2">
                Comprehensive Guidance
              </h3>
              <p className="text-[#736c64]">
                Step-by-step instructions, troubleshooting tips, and beginner-friendly recipes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-[#a8b5a0]/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Heart className="h-6 w-6 text-[#a8b5a0]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1e1919] mb-2">
                Built to Last
              </h3>
              <p className="text-[#736c64]">
                Durable, natural materials that will serve you for countless loaves to come.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#f7f5f2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e1919] mb-4">
              Guidance & Support (That’s Hard to Find)
            </h2>
            <p className="text-lg text-[#736c64] max-w-2xl mx-auto">
              This resource hub is here to walk you through each step—and help you troubleshoot with confidence.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow group">
              <div className="w-12 h-12 bg-[#a8b5a0]/20 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#a8b5a0]/30 transition-colors">
                <BookOpen className="h-6 w-6 text-[#a8b5a0]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1e1919] mb-3">
                Step-by-Step Guidance
              </h3>
              <p className="text-[#736c64] mb-4">
                From creating your starter to baking your first loaf, we&apos;ll guide you through every step with clear, beginner-friendly instructions.
              </p>
              <Link to="/getting-started" className="text-[#a8b5a0] hover:text-[#8fa087] font-medium inline-flex items-center group">
                Get Started
                <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow group">
              <div className="w-12 h-12 bg-[#a8b5a0]/20 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#a8b5a0]/30 transition-colors">
                <Wheat className="h-6 w-6 text-[#a8b5a0]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1e1919] mb-3">
                Beginner-Friendly Recipes
              </h3>
              <p className="text-[#736c64] mb-4">
                Discover simple, foolproof recipes perfect for beginners. From classic sourdough loaves to creative discard recipes.
              </p>
              <Link to="/recipes" className="text-[#a8b5a0] hover:text-[#8fa087] font-medium inline-flex items-center group">
                View Recipes
                <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow group">
              <div className="w-12 h-12 bg-[#a8b5a0]/20 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#a8b5a0]/30 transition-colors">
                <Heart className="h-6 w-6 text-[#a8b5a0]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1e1919] mb-3">
                Troubleshooting & Support
              </h3>
              <p className="text-[#736c64] mb-4">
                Stuck? We&apos;ve got answers. Find solutions to common issues and FAQs to keep your sourdough journey smooth.
              </p>
              <Link to="/troubleshooting" className="text-[#a8b5a0] hover:text-[#8fa087] font-medium inline-flex items-center group">
                Get Help
                <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e1919] mb-4">
              Explore More
            </h2>
            <p className="text-lg text-[#736c64]">
              Dive deeper into the art and science of sourdough baking.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to="/dictionary"
              className="bg-[#f7f5f2] rounded-xl p-6 hover:bg-[#eae8e3] transition-all group"
            >
              <h3 className="font-semibold text-[#1e1919] mb-2 group-hover:text-[#a8b5a0] transition-colors">
                Sourdough Dictionary
              </h3>
              <p className="text-sm text-[#736c64]">
                Learn the terminology and science behind sourdough.
              </p>
            </Link>

            <Link
              to="/care"
              className="bg-[#f7f5f2] rounded-xl p-6 hover:bg-[#eae8e3] transition-all group"
            >
              <h3 className="font-semibold text-[#1e1919] mb-2 group-hover:text-[#a8b5a0] transition-colors">
                Care Guides
              </h3>
              <p className="text-sm text-[#736c64]">
                Keep your tools in perfect condition with our care instructions.
              </p>
            </Link>

            <Link
              to="/faq"
              className="bg-[#f7f5f2] rounded-xl p-6 hover:bg-[#eae8e3] transition-all group"
            >
              <h3 className="font-semibold text-[#1e1919] mb-2 group-hover:text-[#a8b5a0] transition-colors">
                FAQ
              </h3>
              <p className="text-sm text-[#736c64]">
                Quick answers to the most common beginner questions.
              </p>
            </Link>

            <Link
              to="/resources"
              className="bg-[#f7f5f2] rounded-xl p-6 hover:bg-[#eae8e3] transition-all group"
            >
              <h3 className="font-semibold text-[#1e1919] mb-2 group-hover:text-[#a8b5a0] transition-colors">
                Resources
              </h3>
              <p className="text-sm text-[#736c64]">
                Recommended courses, tools, and ingredients.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Email Signup Section */}
      <EmailSignupSection />

      {/* CTA Section */}
      <section className="py-20 bg-[#1e1919] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Baking?
          </h2>
          <p className="text-lg text-[#bbb5ae] mb-8 max-w-2xl mx-auto">
            Your Hearthlowe kit gives you a confident start—plus the step-by-step support to keep going. Let’s turn flour and water into something magical.
          </p>
          <Link to="/getting-started">
            <Button 
              size="lg" 
              className="bg-[#a8b5a0] hover:bg-[#8fa087] text-white px-8 py-6 text-lg group transition-all"
            >
              Begin Your Journey
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
